import styled from '@emotion/styled'
import React from 'react'

type HighlightTextProps = {
  text: string | null | undefined
  children: React.ReactElement
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function highlightNode(
  parent: React.ReactNode | null,
  node: React.ReactNode,
  searchText: string
): React.ReactNode {
  if (!searchText) return node

  if (typeof node === 'string') {
    const escaped = escapeRegExp(searchText)
    const regex = new RegExp(`(${escaped})`, 'gi')
    const parts = node.split(regex)

    return parts.map((part, index) =>
      regex.test(part) ? (
        <HighlightStyled key={index}>{part}</HighlightStyled>
      ) : (
        <React.Fragment key={index}>{part}</React.Fragment>
      )
    )
  }

  if (Array.isArray(node)) {
    return node.map((child, index) => (
      <React.Fragment key={index}>{highlightNode(node, child, searchText)}</React.Fragment>
    ))
  }

  if (React.isValidElement(node)) {
    return React.cloneElement(node, {
      ...node.props,
      children: highlightNode(node, node.props.children, searchText),
    })
  }

  return node
}

export const HighlightText: React.FC<HighlightTextProps> = ({ text, children }) => {
  if (!text) {
    return children
  }
  return highlightNode(null, children, text)
}

// ======
// Styles
// ======

const HighlightStyled = styled.mark({
  color: 'inherit',
  background: '#F2ECF7',
})
